/**
 * Map host catalog payload → portable NextGenPlayer config.
 * No Pentecost+-specific branding hardcoded — reads site configuration + video DTO.
 */

import configuration from "react-global-configuration";

var VOLUME_KEY = "ngp_volume";
var MUTED_KEY = "ngp_muted";

export function readPersistedVolume() {
  try {
    var v = localStorage.getItem(VOLUME_KEY);
    if (v == null) return 1;
    var n = Number(v);
    return isFinite(n) ? Math.max(0, Math.min(1, n)) : 1;
  } catch (e) {
    return 1;
  }
}

export function readPersistedMuted() {
  try {
    return localStorage.getItem(MUTED_KEY) === "1";
  } catch (e) {
    return false;
  }
}

export function persistVolume(volume, muted) {
  try {
    if (volume != null) localStorage.setItem(VOLUME_KEY, String(volume));
    if (muted != null) localStorage.setItem(MUTED_KEY, muted ? "1" : "0");
  } catch (e) {
    /* ignore quota */
  }
}

function truthySetting(v) {
  return v === 1 || v === "1" || v === true || v === "true";
}

/**
 * Normalize Admin watermark_size (%) for TPG Player overlay.
 * Contract: percent of player (.ngp-shell) width, clamped 4–40.
 *
 * @param {string|number|null|undefined} raw
 * @returns {number}
 */
export function normalizeWatermarkSize(raw) {
  var watermarkSize = Number(raw);
  if (!isFinite(watermarkSize) || watermarkSize <= 0) {
    watermarkSize = 12;
  }
  return Math.max(4, Math.min(40, watermarkSize));
}

/**
 * CSS custom property value for watermark width (percent of player width).
 *
 * @param {string|number|null|undefined} raw
 * @returns {string} e.g. "40%"
 */
export function watermarkSizeCssVar(raw) {
  return normalizeWatermarkSize(raw) + "%";
}

export function siteBrand() {
  var label =
    configuration.get("configData.site_name") ||
    configuration.get("configData.app_name") ||
    "";
  var accent =
    configuration.get("configData.primary_color") ||
    configuration.get("configData.theme_color") ||
    "";
  var logo =
    configuration.get("configData.site_logo") ||
    configuration.get("configData.logo") ||
    "";
  // Admin Video Settings → watermark_logo (existing Poletti watermark system)
  var watermark =
    configuration.get("configData.watermark_logo") ||
    configuration.get("configData.watermark") ||
    "";
  var watermarkPosition =
    configuration.get("configData.watermark_position") || "bottom-right";
  var watermarkOpacity = Number(
    configuration.get("configData.watermark_opacity")
  );
  if (!isFinite(watermarkOpacity) || watermarkOpacity <= 0) {
    watermarkOpacity = 0.85;
  }
  return {
    label: label,
    accent: accent,
    logo: logo,
    watermark: watermark,
    watermarkPosition: watermarkPosition,
    watermarkOpacity: Math.max(0.1, Math.min(1, watermarkOpacity)),
    watermarkSize: normalizeWatermarkSize(
      configuration.get("configData.watermark_size")
    ),
  };
}

export function siteFeatures() {
  var castFlag = configuration.get("configData.next_gen_casting");
  var castHeaderFlag = configuration.get("configData.is_cast_system_enabled");
  var casting =
    castFlag === 1 ||
    castFlag === "1" ||
    castFlag === true ||
    String(process.env.REACT_APP_NEXT_GEN_CASTING || "") === "1";
  // Align in-player cast capability with Site Content Cast visibility default ON.
  if (!casting) {
    if (
      castHeaderFlag === undefined ||
      castHeaderFlag === null ||
      castHeaderFlag === ""
    ) {
      casting = true;
    } else {
      casting =
        castHeaderFlag == 1 ||
        castHeaderFlag === true ||
        castHeaderFlag === "1";
    }
  }

  var watermarkEnabled = truthySetting(
    configuration.get("configData.is_watermark_logo_enabled")
  );
  var brandedLoading = truthySetting(
    configuration.get("configData.player_branded_loading")
  );
  // Default branded loading ON when site logo exists (white-label feel)
  if (
    configuration.get("configData.player_branded_loading") == null &&
    (configuration.get("configData.site_logo") ||
      configuration.get("configData.logo"))
  ) {
    brandedLoading = true;
  }

  var theaterDefault = truthySetting(
    configuration.get("configData.player_theater_default")
  );
  var fsOnPlayDefault = truthySetting(
    configuration.get("configData.player_fullscreen_on_play")
  );

  return {
    fullscreen: true,
    pictureInPicture: true,
    playbackSpeed: true,
    keyboardShortcuts: true,
    qualitySelection: true,
    subtitles: true,
    audioTracks: true,
    chapters: false,
    continueWatching: true,
    skipButtons: true,
    skipInterval: 10,
    seekZones: true,
    autoHideControls: true,
    casting: casting,
    chromecast: casting,
    airplay: casting,
    castingSimulate: false,
    tvNavigation: false,
    analytics: true,
    watermarks: watermarkEnabled,
    theaterMode: true,
    theaterDefault: theaterDefault,
    brandedLoading: brandedLoading,
    fullscreenOnFirstPlay: fsOnPlayDefault,
    loadTimeoutMs: 45000,
    /** Modern IMA — requires BE advertising.modern.enabled as well (dual opt-in). */
    modernImaAds:
      truthySetting(configuration.get("configData.modern_ima_ads")) ||
      String(process.env.REACT_APP_MODERN_IMA_ADS || "") === "1",
    liveImaAds:
      truthySetting(configuration.get("configData.live_ima_ads")) ||
      String(process.env.REACT_APP_LIVE_IMA_ADS || "") === "1",
  };
}

/**
 * Normalize playback URLs from API / settings.
 * - Strip unreachable :8081 (media on default HTTPS)
 * - Force https for non-local hosts (preserve http for localhost/LAN)
 * @param {string} url
 * @returns {string}
 */
export function sanitizePlaybackUrl(url) {
  if (!url || typeof url !== "string") {
    return url || "";
  }
  var trimmed = url.trim();
  // Reject non-http(s) schemes (javascript:, data:, file:, blob:, etc.)
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed) && !/^https?:\/\//i.test(trimmed)) {
    return "";
  }
  var out = trimmed.replace(/^(https?:\/\/[^/:]+):8081(?=\/|$)/i, "$1");
  if (/^http:\/\//i.test(out)) {
    try {
      var host = (out.match(/^https?:\/\/([^/:]+)/i) || [])[1] || "";
      host = String(host).toLowerCase();
      var isLocal =
        host === "localhost" ||
        host === "127.0.0.1" ||
        host === "[::1]" ||
        /\.local$/.test(host) ||
        /\.test$/.test(host) ||
        /^192\.168\./.test(host) ||
        /^10\./.test(host) ||
        /^172\.(1[6-9]|2\d|3[0-1])\./.test(host);
      if (!isLocal) {
        out = out.replace(/^http:\/\//i, "https://");
      }
    } catch (e) {
      /* keep original */
    }
  }
  return out;
}

function firstNonEmpty() {
  for (var i = 0; i < arguments.length; i++) {
    var v = arguments[i];
    if (v != null && String(v).trim() !== "") {
      return v;
    }
  }
  return "";
}

/**
 * Normalize YouTube / Vimeo ids or partial URLs into watch/player URLs so
 * detectMediaType + embed providers can select correctly.
 * @param {string} url
 * @param {number|string} [videoType]
 * @returns {string}
 */
export function normalizeEmbedPlaybackUrl(url, videoType) {
  if (!url || typeof url !== "string") {
    return url || "";
  }
  var trimmed = String(url).trim();
  var vt = Number(videoType);
  // Bare YouTube id (11 chars)
  if (
    (vt === 2 || !/^https?:\/\//i.test(trimmed)) &&
    /^[A-Za-z0-9_-]{11}$/.test(trimmed)
  ) {
    return "https://www.youtube.com/watch?v=" + trimmed;
  }
  // Bare Vimeo numeric id
  if ((vt === 4 || !/^https?:\/\//i.test(trimmed)) && /^\d{6,12}$/.test(trimmed)) {
    return "https://vimeo.com/" + trimmed;
  }
  if (vt === 2 && trimmed.indexOf("youtube") === -1 && trimmed.indexOf("youtu.be") === -1) {
    if (/^[A-Za-z0-9_-]{6,}$/.test(trimmed)) {
      return "https://www.youtube.com/watch?v=" + trimmed;
    }
  }
  if (vt === 4 && trimmed.indexOf("vimeo") === -1 && /^\d+$/.test(trimmed)) {
    return "https://vimeo.com/" + trimmed;
  }
  return trimmed;
}

/**
 * Playable URL from mediaMeta and/or Media Asset primary (single source of truth).
 * @param {object} [mediaMeta]
 * @param {object} [mediaAsset]
 * @returns {string}
 */
export function resolvePlayableSrc(mediaMeta, mediaAsset) {
  var fromMeta = mediaMeta && mediaMeta.src ? String(mediaMeta.src).trim() : "";
  if (fromMeta) {
    return sanitizePlaybackUrl(
      normalizeEmbedPlaybackUrl(fromMeta, mediaMeta && mediaMeta.videoType)
    );
  }
  var primary =
    mediaAsset && mediaAsset.primary && mediaAsset.primary.url
      ? String(mediaAsset.primary.url).trim()
      : "";
  if (!primary) {
    return "";
  }
  var vt =
    (mediaAsset.metadata && mediaAsset.metadata.video_type) ||
    (mediaMeta && mediaMeta.videoType);
  return sanitizePlaybackUrl(normalizeEmbedPlaybackUrl(primary, vt));
}

/**
 * Map Media Asset sourceType → engine MediaType string for provider selection.
 * @param {string} [sourceType]
 * @returns {string|null}
 */
export function mediaSourceTypeToEngineType(sourceType) {
  if (!sourceType) return null;
  var s = String(sourceType).toLowerCase();
  if (s === "youtube") return "youtube";
  if (s === "vimeo") return "vimeo";
  if (s === "hosted" || s === "external_url") return null;
  return null;
}

/**
 * Resolve stream URL + metadata from location.state video DTO.
 * @param {object} videoDetailsFirst
 * @param {object} [opts]
 * @param {string} [opts.videoFrom] e.g. "trailer"
 */
export function resolveMediaFromVideoDetails(videoDetailsFirst, opts) {
  opts = opts || {};
  var videoFrom = opts.videoFrom;
  var mainVideo;
  var subTitle;
  var videoTitle;
  var poster;

  if (videoFrom != null && videoFrom !== "") {
    subTitle = videoDetailsFirst.video_subtitle_vtt;
    if (videoFrom === "trailer") {
      mainVideo = firstNonEmpty(
        videoDetailsFirst.resolutions &&
          videoDetailsFirst.resolutions.original,
        videoDetailsFirst.trailer_video,
        videoDetailsFirst.main_video
      );
      subTitle = videoDetailsFirst.trailer_subtitle;
    } else {
      mainVideo = firstNonEmpty(
        videoDetailsFirst.resolutions &&
          videoDetailsFirst.resolutions.original,
        videoDetailsFirst.main_resolutions &&
          videoDetailsFirst.main_resolutions.original,
        videoDetailsFirst.main_video,
        videoDetailsFirst.video
      );
    }
    videoTitle = videoDetailsFirst.name || videoDetailsFirst.title;
  } else {
    mainVideo = firstNonEmpty(
      videoDetailsFirst.main_video,
      videoDetailsFirst.video,
      videoDetailsFirst.main_resolutions &&
        videoDetailsFirst.main_resolutions.original,
      videoDetailsFirst.resolutions && videoDetailsFirst.resolutions.original,
      videoDetailsFirst.hls_main_video
    );
    subTitle = videoDetailsFirst.video_subtitle_vtt;
    videoTitle = videoDetailsFirst.title || videoDetailsFirst.name;
  }

  mainVideo = sanitizePlaybackUrl(
    normalizeEmbedPlaybackUrl(mainVideo, videoDetailsFirst.video_type)
  );
  poster = videoDetailsFirst.default_image || videoDetailsFirst.poster || "";

  var subtitles = [];
  if (subTitle) {
    subtitles.push({
      id: "host-sub-0",
      label: "Subtitles",
      language: "en",
      src: subTitle,
      // Product: captions default Off; user opts in via control.
      default: false,
    });
  }

  return {
    src: mainVideo,
    title: videoTitle || "",
    poster: poster,
    subtitles: subtitles,
    contentId: String(
      videoDetailsFirst.admin_video_id ||
        videoDetailsFirst.id ||
        mainVideo ||
        ""
    ),
    seekTime: Number(videoDetailsFirst.seek_time_in_seconds) || 0,
    videoType: videoDetailsFirst.video_type,
  };
}

/**
 * @param {object} videoDetailsFirst
 * @param {object} [options]
 * @param {object} [options.controlPlane]  from resolveHostControlPlane
 * @returns {object} NextGenPlayer config
 */
export function buildHostPlayerConfig(videoDetailsFirst, options) {
  options = options || {};
  var plane = options.controlPlane || null;
  var media =
    (plane && plane.mediaMeta) ||
    resolveMediaFromVideoDetails(videoDetailsFirst, {
      videoFrom: options.videoFrom,
    });
  var mediaAsset = (plane && plane.mediaAsset) || options.mediaAsset || null;
  var playableSrc = resolvePlayableSrc(media, mediaAsset);
  if (playableSrc && (!media || media.src !== playableSrc)) {
    media = Object.assign({}, media || {}, { src: playableSrc });
  }
  var brand = Object.assign({}, siteBrand(), options.branding || {});
  var features = Object.assign(
    {},
    siteFeatures(),
    (plane && plane.features) || {},
    options.features || {}
  );
  var plugins = Array.isArray(options.plugins) ? options.plugins : [];

  var startTime = 0;
  if (options.applySeek !== false && media.seekTime > 0 && !options.isTrailer) {
    startTime = media.seekTime;
  }

  var explicitType =
    mediaSourceTypeToEngineType(mediaAsset && mediaAsset.sourceType) ||
    (Number(media.videoType) === 2
      ? "youtube"
      : Number(media.videoType) === 4
        ? "vimeo"
        : null);

  var source =
    media.src && explicitType
      ? { src: media.src, type: explicitType }
      : media.src;

  return {
    source: source,
    poster: media.poster,
    title: media.title,
    contentId: media.contentId,
    subtitles: media.subtitles,
    audioTracks: options.audioTracks || [],
    chapters: options.chapters || [],
    plugins: plugins,
    branding: brand,
    theme: Object.assign(
      {
        colors: {
          accent: brand.accent || "#e50914",
        },
      },
      options.theme || {}
    ),
    casting: Object.assign(
      {
        receiverApplicationId:
          configuration.get("configData.chromecast_receiver_id") || "",
        chromecast: true,
        airplay: true,
        simulate: false,
        brand: { castButtonLabel: "Cast" },
      },
      options.casting || {}
    ),
    features: features,
    autoplay: options.autoplay !== false,
    volume: options.volume != null ? options.volume : readPersistedVolume(),
    muted: options.muted != null ? options.muted : readPersistedMuted(),
    startTime: options.startTime != null ? options.startTime : startTime,
    debug: !!options.debug,
    sessionId: options.sessionId || null,
    sessionStartedAt: options.sessionStartedAt || null,
    /** Phase B3 control plane — drives Facade / strategy / capabilities */
    mediaAsset: mediaAsset,
    presentationStrategy:
      (plane && plane.strategy) || options.presentationStrategy || null,
    enabledCapabilities:
      (plane && plane.enabledCapabilities) ||
      options.enabledCapabilities ||
      [],
  };
}

export default buildHostPlayerConfig;
